import { useEffect, useState } from "react";
import { api } from "./lib/api";

interface CloudflareData {
  configured: boolean;
  projectName: string | null;
  deployment: {
    url: string;
    environment: string;
    createdAt: string;
    status: string;
  } | null;
  traffic: {
    totals: { requests: number; pageViews: number; dailyUniquesSum: number };
  } | null;
  analyticsStatus?:
    "available" | "domain_missing" | "zone_not_found" | "query_failed";
}
const number = new Intl.NumberFormat("it-IT");

export default function CloudflarePanel({ siteId }: { siteId: string }) {
  const [data, setData] = useState<CloudflareData | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api<CloudflareData>(`/api/sites/${siteId}/integrations/cloudflare`)
      .then(setData)
      .catch((cause) =>
        setError(
          cause instanceof Error
            ? cause.message
            : "Dati Cloudflare non disponibili",
        ),
      );
  }, [siteId]);
  if (error)
    return (
      <section className="cf-panel">
        <div className="notice">{error}</div>
      </section>
    );
  if (!data) return <section className="cf-panel">Lettura Cloudflare…</section>;
  return (
    <section className="cf-panel">
      <div className="cf-heading">
        <div>
          <p className="eyebrow indigo">Cloudflare</p>
          <h3>Pubblicazione e traffico</h3>
          <p>
            {data.configured
              ? "Dati letti direttamente dalle API Cloudflare."
              : "Il sito usa Cloudflare, ma il token Analytics non è ancora configurato nel Worker."}
          </p>
        </div>
        <span className="integration-state">
          {data.configured ? "Collegato" : "Da configurare"}
        </span>
      </div>
      {data.deployment && (
        <div className="cf-deploy">
          <strong>Ultimo deploy: {data.deployment.status}</strong>
          <span>
            {new Intl.DateTimeFormat("it-IT", {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(new Date(data.deployment.createdAt))}
          </span>
          <a href={data.deployment.url} target="_blank" rel="noreferrer">
            Apri anteprima
          </a>
        </div>
      )}
      {data.traffic && (
        <div className="gsc-metrics">
          <article>
            <small>Visite pagina</small>
            <strong>{number.format(data.traffic.totals.pageViews)}</strong>
          </article>
          <article title="Somma degli utenti unici rilevati in ciascun giorno, non utenti unici mensili">
            <small>Unici giornalieri, somma</small>
            <strong>
              {number.format(data.traffic.totals.dailyUniquesSum)}
            </strong>
          </article>
          <article>
            <small>Richieste</small>
            <strong>{number.format(data.traffic.totals.requests)}</strong>
          </article>
        </div>
      )}
      {data.configured && !data.traffic && (
        <div className="advanced-note">
          <strong>Traffico non disponibile</strong>
          <p>
            {data.analyticsStatus === "domain_missing"
              ? "Aggiungi il dominio del sito per individuare la zona Cloudflare corretta."
              : data.analyticsStatus === "zone_not_found"
                ? "Il dominio non risulta in una zona accessibile al token Cloudflare configurato."
                : "Cloudflare non ha restituito i dati Analytics. Verifica i permessi del token."}
          </p>
        </div>
      )}
    </section>
  );
}
